import { Suspense, useCallback } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { WARNING_DIALOG } from '~common';
import { WarningDialog } from '~components/atoms/custom';
import { Scanner } from '~components/molecules/Scanner';
import { useModal } from '~hooks';
import { useCameraPermission } from '~providers';
import {
  PINNING_STEP,
  usePinningStepContext,
} from '~providers/PinningStepProvider';
import { IFormCreationValues } from '~providers/useForm';
import { postRequest } from '~services';
import {
  CameraPermissionStatus,
  NewQueryResponse,
  NotShowToastError,
  UnassignedStation,
} from '~types';

export const ScanQRStep: React.FC = () => {
  const {
    isOpen: isStationNotFoundAlertOpen,
    openModal: openStationNotFoundAlert,
    closeModal: closeStationNotFoundAlert,
  } = useModal();

  const { handleSetPinningStep, goToNextStep } = usePinningStepContext();

  const { setFieldValue } = useFormikContext<IFormCreationValues>();

  const getStations = (
    scannedSerialNo: string,
  ): Promise<NewQueryResponse<UnassignedStation>> =>
    postRequest({
      path: 'api/station/list-with-unassigned-stations',
      data: {
        search: scannedSerialNo,
        page: 0,
        size: 1,
      },
    }).then((res) => res.data);

  const onScanCallback = useCallback(
    async (url: string) => {
      const queryParams = new URLSearchParams(new URL(url).search);
      const serialNo = queryParams.get('box');
      if (serialNo) {
        const response = await getStations(serialNo);
        const options =
          response.data.map((elem) => ({
            id: elem.id,
            name: elem.imei,
            imei: elem.imei,
            serialNumber: elem.serialNumber,
            type: elem.type,
          })) ?? [];

        if (options.length === 0) {
          openStationNotFoundAlert();
          // Should not show error toast for this specific error
          throw new NotShowToastError('Station not found');
        }

        setFieldValue('placement.station', options[0]);

        handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SELECT_STATION);
      }
    },
    [handleSetPinningStep, openStationNotFoundAlert, setFieldValue],
  );

  const { latestPermissionStatus, handleSetLatestPermissionStatus } =
    useCameraPermission();

  const onLoadingScannerErrorCallback = useCallback(
    (onErrorPermissionStatus: CameraPermissionStatus) => {
      if (latestPermissionStatus !== onErrorPermissionStatus) {
        handleSetLatestPermissionStatus(onErrorPermissionStatus);
        goToNextStep();
      }
    },
    [goToNextStep, handleSetLatestPermissionStatus, latestPermissionStatus],
  );

  return (
    <>
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography variant="h6" fontWeight={600}>
            Scan station's QR code
          </Typography>
          <Typography variant="body1" fontWeight={400}>
            Position the QR code within the frame and we’ll scan it
            automatically.
          </Typography>
        </Stack>

        <Suspense fallback={<Skeleton />}>
          <Scanner
            onScanCallback={onScanCallback}
            onLoadingScannerErrorCallback={onLoadingScannerErrorCallback}
          />
        </Suspense>
      </Stack>
      <Box flex={1}>
        <Typography
          variant="body1"
          color="#008FE1"
          fontWeight={500}
          onClick={goToNextStep}
          align="center"
        >
          Scan not working? Search by IMEI
        </Typography>
      </Box>
      {/* ALERT STATION IS NOT FOUND */}
      <WarningDialog
        open={isStationNotFoundAlertOpen}
        onClose={closeStationNotFoundAlert}
        aria-labelledby="station-not-found-alert-dialog"
        title={WARNING_DIALOG.STATION_NOT_FOUND.TITLE}
        content={WARNING_DIALOG.STATION_NOT_FOUND.CONTENT}
      />
    </>
  );
};
